import * as Y from 'yjs';
import type { CanvasDiffResult } from '@/lib/diff/types';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '@/features/canvas/engine/wiring/canvas-roots';

// Re-export so consumers can still import CanvasDiffResult from this engine path.
export type { CanvasDiffResult } from '@/lib/diff/types';

// Frames are container nodes whose Yjs `size` field can be mutated by React
// Flow's resize-observer when the frame collapses/expands (the rendered size
// snaps between the collapsed folder dimensions and the authored expanded
// layout). Comparing frame size across snapshots therefore produces false
// positives — a pure collapse/expand with no user-initiated move would show
// up as "moved". We track frames by position only; their size is intentionally
// excluded from the diff.
//
// Additionally, RF's parent-extent clamp can corrupt child positions in Yjs
// during the collapse/expand transition before the guard in useYjsNodeSync
// drops those writes. If a child's parent frame is collapsed in EITHER doc,
// we suppress it from `moved` detection — the position difference is noise.
// added/deleted still fire for children regardless (structural changes are real).
const POSITION_ONLY_TYPES = new Set(['frame']);

type NodeGeometry = { x: number; y: number; width: number; height: number; positionOnly: boolean; parentId?: string };

export function computeCanvasDiff(
  currentDoc: Y.Doc,
  snapshotDoc: Y.Doc,
  binding: CanvasBinding = TOP_LEVEL_BINDING,
): CanvasDiffResult {
  const currentRootNodes = canvasRoots(currentDoc, binding).nodes;
  const snapshotRootNodes = canvasRoots(snapshotDoc, binding).nodes;
  const currentNodes = parseNodesMap(currentRootNodes);
  const snapshotNodes = parseNodesMap(snapshotRootNodes);

  // Build the union of collapsed frame IDs across both docs so we can
  // suppress moved-detection for children of any collapsed frame in either.
  const collapsedInCurrent = collapsedFrameIds(currentRootNodes);
  const collapsedInSnapshot = collapsedFrameIds(snapshotRootNodes);

  const added = new Set<string>();
  const deleted = new Set<string>();
  const moved = new Set<string>();

  for (const [id, currentGeo] of currentNodes) {
    const snapGeo = snapshotNodes.get(id);
    if (!snapGeo) {
      added.add(id);
    } else {
      const parentId = currentGeo.parentId ?? snapGeo.parentId;
      const parentCollapsed = parentId !== undefined &&
        (collapsedInCurrent.has(parentId) || collapsedInSnapshot.has(parentId));
      if (!parentCollapsed && geometryChanged(currentGeo, snapGeo)) {
        moved.add(id);
      }
    }
  }

  for (const id of snapshotNodes.keys()) {
    if (!currentNodes.has(id)) {
      deleted.add(id);
    }
  }

  return { added, deleted, moved };
}

function parseNodesMap(map: Y.Map<Y.Map<unknown>>): Map<string, NodeGeometry> {
  const result = new Map<string, NodeGeometry>();
  map.forEach((value) => {
    if (!(value instanceof Y.Map)) return;
    const id = value.get('id');
    const type = value.get('type');
    const position = value.get('position');
    const size = value.get('size');
    const parentId = value.get('parentId');
    if (typeof id !== 'string') return;
    if (!position || typeof (position as { x: unknown }).x !== 'number' || typeof (position as { y: unknown }).y !== 'number') return;
    const pos = position as { x: number; y: number };
    const positionOnly = typeof type === 'string' && POSITION_ONLY_TYPES.has(type);
    const sz = (!positionOnly && size && typeof (size as { width: unknown }).width === 'number' && typeof (size as { height: unknown }).height === 'number')
      ? size as { width: number; height: number }
      : { width: 0, height: 0 };
    result.set(id, {
      x: pos.x, y: pos.y, width: sz.width, height: sz.height,
      positionOnly,
      parentId: typeof parentId === 'string' ? parentId : undefined,
    });
  });
  return result;
}

/** Returns the set of frame IDs that have data.collapsed === true in this map. */
function collapsedFrameIds(map: Y.Map<Y.Map<unknown>>): Set<string> {
  const ids = new Set<string>();
  map.forEach((value) => {
    if (!(value instanceof Y.Map)) return;
    if (value.get('type') !== 'frame') return;
    const data = value.get('data');
    if (data instanceof Y.Map && data.get('collapsed') === true) {
      const id = value.get('id');
      if (typeof id === 'string') ids.add(id);
    }
  });
  return ids;
}

function geometryChanged(a: NodeGeometry, b: NodeGeometry): boolean {
  if (a.positionOnly || b.positionOnly) return a.x !== b.x || a.y !== b.y;
  return a.x !== b.x || a.y !== b.y || a.width !== b.width || a.height !== b.height;
}
